import React, { useState } from 'react';
import { toast } from 'react-toastify';
import SimpleCard from '../../../components/templates/SimpleCard';
import Button from '../../../components/Button';
import FileCard from '../../../components/templates/FileCard';
import Modal, { ModalHead, ModalWrapper, ModalBody } from '../../../components/Modal';
import corporateService from '../../../axios/services/corporateService';
import styles from '../corporate.module.scss';
import pakaiClass from 'pakai-class';

const UploadContractCard = ({ item, index, getDetail }) => {
  const [showModal, setShowModal] = useState(false);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [contractFile, setContractFile] = useState(null);
  const [rejectReason, setRejectReason] = useState('');

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file type
      if (file.type !== 'application/pdf') {
        toast('File kontrak harus berformat PDF', { type: 'error' });
        return;
      }
      
      // Validate file size (10MB)
      const maxSize = 10 * 1024 * 1024;
      if (file.size > maxSize) {
        toast('Ukuran file kontrak maksimal 10MB', { type: 'error' });
        return;
      }

      setContractFile(file);
    }
  };

  const uploadContract = () => {
    if (!contractFile) {
      toast('Pilih file kontrak terlebih dahulu', { type: 'error' });
      return;
    }

    setIsLoading(true);
    const formData = new FormData();
    formData.append('contract', contractFile);

    corporateService.uploadContract(index, formData)
      .then(() => {
        setShowModal(false);
        setContractFile(null);
        getDetail();
        toast('Kontrak berhasil diupload', { type: 'success' });
      })
      .catch((e) => {
        toast(e.response?.data?.message || 'Gagal upload kontrak', { type: 'error' });
      })
      .finally(() => setIsLoading(false));
  };

  const approveContract = () => {
    setIsLoading(true);
    corporateService.approveContract(index)
      .then(() => {
        setShowApprovalModal(false);
        getDetail();
        toast('Kontrak berhasil disetujui', { type: 'success' });
      })
      .catch((e) => {
        toast(e.response?.data?.message || 'Gagal menyetujui kontrak', { type: 'error' });
      })
      .finally(() => setIsLoading(false));
  };

  const rejectContract = () => {
    if (!rejectReason.trim()) {
      toast('Alasan penolakan harus diisi', { type: 'error' });
      return;
    }

    setIsLoading(true);
    corporateService.rejectContract(index, { reason: rejectReason })
      .then(() => {
        setShowRejectModal(false);
        setRejectReason('');
        getDetail();
        toast('Kontrak berhasil ditolak', { type: 'success' });
      })
      .catch((e) => {
        toast(e.response?.data?.message || 'Gagal menolak kontrak', { type: 'error' });
      })
      .finally(() => setIsLoading(false));
  };

  const contract = item?.company?.archive?.contract;
  const hasTemplate = contract?.file;
  const hasSignedContract = contract?.signedFile;
  const isApproved = contract?.status === 'APPROVED';
  const isRejected = contract?.status === 'REJECTED';
  const isPendingApproval = contract?.status === 'SIGNED_UPLOADED';

  return (
    <>
      <SimpleCard className="mb-24">
        <div>
          <div className="d-flex justify-content-between align-items-center mb-12">
            <p className="font-500">Kontrak Perusahaan</p>
            <div className="d-flex gap-2">
              {!hasTemplate && (
                <Button
                  variant="primary"
                  size="medium"
                  onClick={() => setShowModal(true)}
                  icon="📄"
                >
                  Upload Template Kontrak
                </Button>
              )}
              {hasSignedContract && isPendingApproval && (
                <>
                  <Button
                    variant="primary"
                    size="medium"
                    onClick={() => setShowApprovalModal(true)}
                    icon="✅"
                    style={{ marginRight: '12px' }}
                  >
                    Setujui
                  </Button>
                  <Button
                    variant="secondary"
                    size="medium"
                    onClick={() => setShowRejectModal(true)}
                    icon="❌"
                  >
                    Tolak
                  </Button>
                </>
              )}
            </div>
          </div>
          <hr />
          
          {hasTemplate && (
            <div className="mt-16">
              <div className="d-flex justify-content-between align-items-center mb-12">
                <h6>Template Kontrak</h6>
                <span className="badge badge-info">Template Ready</span>
              </div>
              <FileCard
                className="mb-12 modern"
                title="Template Kontrak"
                filename="kontrak-template.pdf"
                url={contract.file.url}
                size="medium"
              />
              <small className="text-muted">
                Diupload pada: {new Date(contract.uploadedAt).toLocaleDateString('id-ID')}
              </small>
            </div>
          )}

          {hasSignedContract && (
            <div className="mt-16">
              <div className="d-flex justify-content-between align-items-center mb-12">
                <h6>Kontrak Tertandatangan</h6>
                <span className={`badge ${
                  isApproved ? 'badge-success' : 
                  isRejected ? 'badge-danger' : 
                  'badge-warning'
                }`}>
                  {isApproved ? 'Disetujui' : 
                   isRejected ? 'Ditolak' : 
                   'Menunggu Persetujuan'}
                </span>
              </div>
              <FileCard
                className="mb-12 modern"
                title="Kontrak Tertandatangan"
                filename="kontrak-signed.pdf"
                url={contract.signedFile.url}
                size="medium"
              />
              <small className="text-muted mb-8 d-block">
                Diupload pada: {new Date(contract.signedUploadedAt).toLocaleDateString('id-ID')}
              </small>
              {isRejected && contract.rejectionReason && (
                <div className="alert alert-danger">
                  <strong>Alasan Penolakan:</strong> {contract.rejectionReason}
                </div>
              )}
              {isApproved && contract.approvalDate && (
                <div className="alert alert-success">
                  <strong>Disetujui pada:</strong> {new Date(contract.approvalDate).toLocaleDateString('id-ID')}
                </div>
              )}
            </div>
          )}

          {!hasTemplate && !hasSignedContract && (
            <div className="mt-16 text-center">
              <i className="tim-icons icon-upload" style={{ fontSize: '3rem', color: '#ccc' }}></i>
              <p className="mb-16 text-muted mt-3">Belum ada kontrak yang diupload</p>
              <p className="text-muted small">Upload template kontrak untuk memulai proses</p>
            </div>
          )}
        </div>
      </SimpleCard>

      <Modal in={showModal} onClose={() => setShowModal(false)}>
        <ModalWrapper>
          <ModalHead 
            title="Upload Template Kontrak" 
            onClose={() => setShowModal(false)} 
          />
          <ModalBody>
            <div className="mb-16">
              <label className="font-500 mb-8 d-block">
                File Template Kontrak (PDF, maksimal 10MB)
              </label>
              <input
                type="file"
                accept=".pdf"
                onChange={handleFileSelect}
                className="form-control"
              />
              {contractFile && (
                <small className="text-success mt-8 d-block">
                  File dipilih: {contractFile.name}
                </small>
              )}
            </div>
            <div className="alert alert-info">
              <small>
                <strong>Catatan:</strong>
                <ul>
                  <li>File harus berformat PDF</li>
                  <li>Ukuran maksimal 10MB</li>
                  <li>Template akan dikirim ke user untuk ditandatangani</li>
                </ul>
              </small>
            </div>
            <div className="d-flex justify-content-end gap-2 mt-24">
              <Button
                variant="secondary"
                size="medium"
                onClick={() => setShowModal(false)}
                disabled={isLoading}
                className="mr-8"
              >
                Batal
              </Button>
              <Button
                variant="primary"
                size="medium"
                onClick={uploadContract}
                loading={isLoading}
                disabled={!contractFile}
                icon="📤"
              >
                Upload Template
              </Button>
            </div>
          </ModalBody>
        </ModalWrapper>
      </Modal>

      {/* Approval Modal */}
      <Modal in={showApprovalModal} onClose={() => setShowApprovalModal(false)}>
        <ModalWrapper>
          <ModalHead 
            title="Setujui Kontrak" 
            onClose={() => setShowApprovalModal(false)} 
          />
          <ModalBody>
            <div className="mb-16">
              <p>Apakah Anda yakin ingin menyetujui kontrak yang telah ditandatangani ini?</p>
              <div className="alert alert-warning">
                <small>
                  <strong>Perhatian:</strong> Setelah disetujui, user tidak akan bisa mengupload kontrak lagi.
                </small>
              </div>
            </div>
            <div className="d-flex justify-content-end gap-2 mt-24">
              <Button
                variant="secondary"
                size="medium"
                onClick={() => setShowApprovalModal(false)}
                disabled={isLoading}
                className="mr-8"
              >
                Batal
              </Button>
              <Button
                variant="success"
                size="medium"
                onClick={approveContract}
                loading={isLoading}
                icon="✅"
              >
                Setujui Kontrak
              </Button>
            </div>
          </ModalBody>
        </ModalWrapper>
      </Modal>

      {/* Rejection Modal */}
      <Modal in={showRejectModal} onClose={() => setShowRejectModal(false)}>
        <ModalWrapper>
          <ModalHead 
            title="Tolak Kontrak" 
            onClose={() => setShowRejectModal(false)} 
          />
          <ModalBody>
            <div className="mb-16">
              <label className="font-500 mb-8 d-block">
                Alasan Penolakan *
              </label>
              <textarea
                className="form-control"
                rows="4"
                placeholder="Masukkan alasan penolakan kontrak..."
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
              />
            </div>
            <div className="alert alert-info">
              <small>
                <strong>Catatan:</strong> User akan menerima notifikasi penolakan beserta alasan dan bisa mengupload ulang kontrak yang sudah diperbaiki.
              </small>
            </div>
            <div className="d-flex justify-content-end gap-2 mt-24">
              <Button
                variant="secondary"
                size="medium"
                onClick={() => setShowRejectModal(false)}
                disabled={isLoading}
                className="mr-8"
              >
                Batal
              </Button>
              <Button
                variant="danger"
                size="medium"
                onClick={rejectContract}
                loading={isLoading}
                disabled={!rejectReason.trim()}
                icon="❌"
              >
                Tolak Kontrak
              </Button>
            </div>
          </ModalBody>
        </ModalWrapper>
      </Modal>
    </>
  );
};

export default UploadContractCard;
